<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import { useChannelIconFrame } from 'next/icon/provider';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import { INBOX_TYPES, TWILIO_CHANNEL_MEDIUM } from 'dashboard/helper/inbox';
import { useAccount } from 'dashboard/composables/useAccount';
import { isApiInboxDisabledForAccount } from './apiInboxVisibility';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();

const showDeletePopup = ref(false);
const deleteMode = ref('migrate');
const deleteTargetInboxId = ref('');
const showMigratePopup = ref(false);
const selectedInbox = ref({});
const migrationSourceInbox = ref({});
const migrationTargetInboxId = ref(null);
const isMigratingData = ref(false);
const isLoadingInboxes = ref(true);
const searchQuery = ref('');
const healthByInbox = ref({});
const loadedInboxIds = new Set();
const loadedHealthIds = new Set();
const failedInboxAvatarIds = ref({});

const inboxes = useMapGetter('inboxes/getInboxes');

const listCopy = {
  quality: 'Qualidade',
  connectedBot: 'Bot conectado',
  noBot: 'Sem bot',
  editBot: 'Editar bot',
  editCollaborators: 'Editar agentes e departamentos',
  migrateData: 'Migrar dados',
  migrateModalTitle: 'Migrar dados da caixa de entrada',
  migrateModalDescription:
    'Selecione a caixa de entrada de destino para mover contatos, conversas e mensagens.',
  migrateTargetLabel: 'Caixa de destino',
  migrateTargetPlaceholder: 'Selecione uma caixa de entrada',
  migrateConfirm: 'Migrar dados',
  migrateCancel: 'Cancelar',
  migrateSuccess: 'Migração iniciada com sucesso.',
  migrateError: 'Não foi possível iniciar a migração.',
  migrateValidationError: 'Selecione uma caixa de destino para continuar.',
  hardDeletePrompt:
    'Deseja apagar todos os contatos e mensagens desta caixa de entrada? Esta ação é permanente.',
  deleteModalTitle: 'Excluir caixa de entrada',
  deleteModalDescription:
    'Escolha como deseja excluir esta caixa. Você pode migrar os dados para outra caixa ou apagar tudo permanentemente.',
  deleteModeLabel: 'Como deseja excluir?',
  deleteModeMigrate: 'Migrar contatos e mensagens para outra caixa',
  deleteModeHardDelete: 'Apagar todos os contatos e mensagens (permanente)',
  deleteTargetLabel: 'Caixa de destino para migração',
  deleteTargetPlaceholder: 'Selecione uma caixa de entrada',
  deleteConfirm: 'Confirmar exclusão',
  deleteCancel: 'Cancelar',
  selectedInbox: 'Caixa selecionada',
  deleteValidationError:
    'Selecione uma caixa de destino para continuar com a migração.',
  incompatibleTargetError:
    'A caixa selecionada nao e compativel para migracao.',
  deleteScheduledSuccess:
    'Exclusão iniciada. A caixa será removida após a limpeza dos dados.',
};

const isPlainApiInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.API &&
  !inbox.additional_attributes?.is_evolution_api_integration;

const isEvolutionApiWhatsappInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.API &&
  inbox.additional_attributes?.is_evolution_api_integration;

const isTwilioWhatsappInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.TWILIO &&
  inbox.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP;

const isWhatsappFamilyInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.WHATSAPP ||
  isEvolutionApiWhatsappInbox(inbox) ||
  isTwilioWhatsappInbox(inbox);

const isMigrationCompatible = (sourceInbox, targetInbox) => {
  if (!sourceInbox || !targetInbox) return false;

  if (sourceInbox.channel_type === targetInbox.channel_type) return true;

  return (
    isWhatsappFamilyInbox(sourceInbox) && isWhatsappFamilyInbox(targetInbox)
  );
};

const inboxesList = computed(() => {
  const visibleInboxes = isApiInboxDisabledForAccount(accountId.value)
    ? inboxes.value?.filter(inbox => !isPlainApiInbox(inbox))
    : inboxes.value;

  return visibleInboxes?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, [
    'name',
    'channel_type',
    'medium',
  ]);
});

const categoryLabels = {
  whatsapp: 'Conexões WhatsApp',
  instagram: 'Conexões Instagram',
  site: 'Conexões Site',
  telegram: 'Conexões Telegram',
  tiktok: 'Conexões TikTok',
  messenger: 'Conexões Messenger',
  email: 'Conexões E-mail',
  sms: 'Conexões SMS',
  voice: 'Conexões de Voz',
  api: 'Conexões API',
  line: 'Conexões Line',
  twitter: 'Conexões X',
  other: 'Outras conexões',
};

const categoryDescriptions = {
  whatsapp: 'Canais usados para atendimento e automações no WhatsApp.',
  instagram: 'Contas conectadas para conversas originadas no Instagram.',
  site: 'Widgets e canais de chat instalados em sites.',
  telegram: 'Bots e canais conectados ao Telegram.',
  tiktok: 'Contas conectadas para interações do TikTok.',
  messenger: 'Páginas e canais conectados ao Facebook Messenger.',
  email: 'Caixas de e-mail conectadas para atendimento.',
  sms: 'Canais de SMS e números conectados.',
  voice: 'Canais habilitados para chamadas de voz.',
  api: 'Canais personalizados conectados via API.',
  line: 'Contas conectadas ao Line.',
  twitter: 'Perfis conectados ao X.',
  other: 'Canais que não se encaixam nas categorias principais.',
};

const categoryOrder = [
  'whatsapp',
  'instagram',
  'site',
  'telegram',
  'tiktok',
  'messenger',
  'email',
  'sms',
  'voice',
  'api',
  'line',
  'twitter',
  'other',
];

const getInboxCategory = inbox => {
  const isEvolutionApiInbox =
    inbox.channel_type === INBOX_TYPES.API &&
    inbox.additional_attributes?.is_evolution_api_integration;

  if (isEvolutionApiInbox) return 'whatsapp';

  if (
    inbox.channel_type === INBOX_TYPES.TWILIO &&
    inbox.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP
  ) {
    return 'whatsapp';
  }

  if (
    inbox.channel_type === INBOX_TYPES.TWILIO &&
    inbox.voice_enabled &&
    inbox.medium !== TWILIO_CHANNEL_MEDIUM.WHATSAPP
  ) {
    return 'voice';
  }

  const categoryByChannelType = {
    [INBOX_TYPES.WHATSAPP]: 'whatsapp',
    [INBOX_TYPES.INSTAGRAM]: 'instagram',
    [INBOX_TYPES.WEB]: 'site',
    [INBOX_TYPES.TELEGRAM]: 'telegram',
    [INBOX_TYPES.TIKTOK]: 'tiktok',
    [INBOX_TYPES.FB]: 'messenger',
    [INBOX_TYPES.EMAIL]: 'email',
    [INBOX_TYPES.SMS]: 'sms',
    [INBOX_TYPES.TWILIO]: 'sms',
    [INBOX_TYPES.API]: 'api',
    [INBOX_TYPES.LINE]: 'line',
    [INBOX_TYPES.TWITTER]: 'twitter',
  };

  return categoryByChannelType[inbox.channel_type] || 'other';
};

const activeBotForInbox = inbox =>
  store.getters['agentBots/getActiveAgentBot'](inbox.id) || {};

const activeBotName = inbox => activeBotForInbox(inbox).name || listCopy.noBot;

const qualityValueForInbox = inbox => {
  const healthData = healthByInbox.value[inbox.id] || {};
  const additionalAttributes = inbox.additional_attributes || {};
  const providerConfig = inbox.provider_config || {};

  return (
    healthData.quality_rating ||
    additionalAttributes.quality_rating ||
    additionalAttributes.quality ||
    providerConfig.quality_rating ||
    null
  );
};

const isWhatsAppLikeInbox = inbox => getInboxCategory(inbox) === 'whatsapp';

const qualityLabelForInbox = inbox => {
  if (!isWhatsAppLikeInbox(inbox)) return '';
  return qualityValueForInbox(inbox) || 'N/A';
};

const qualityTextForInbox = inbox =>
  `${listCopy.quality}: ${qualityLabelForInbox(inbox)}`;

const qualityClassForInbox = inbox => {
  const quality = qualityValueForInbox(inbox);
  if (quality === 'GREEN') return 'bg-n-teal-3 text-n-teal-11';
  if (quality === 'YELLOW') return 'bg-n-amber-3 text-n-amber-11';
  if (quality === 'RED') return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-alpha-2 text-n-slate-11';
};

const canFetchHealthForInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.WHATSAPP &&
  (inbox.provider === 'whatsapp_cloud' ||
    inbox.provider_config?.source === 'embedded_signup' ||
    inbox.provider_config?.phone_number_id);

const loadInboxBot = async inbox => {
  try {
    await store.dispatch('agentBots/fetchAgentBotInbox', inbox.id);
  } catch {
    // Caixa sem bot conectado ou endpoint indisponível.
  }
};

const loadInboxHealth = async inbox => {
  if (!canFetchHealthForInbox(inbox) || loadedHealthIds.has(inbox.id)) return;
  loadedHealthIds.add(inbox.id);

  try {
    const { data } = await InboxHealthAPI.getHealthStatus(inbox.id);
    healthByInbox.value = {
      ...healthByInbox.value,
      [inbox.id]: data,
    };
  } catch {
    healthByInbox.value = {
      ...healthByInbox.value,
      [inbox.id]: {},
    };
  }
};

const loadInboxExtras = async list => {
  if (!list?.length) return;

  list.forEach(inbox => {
    if (loadedInboxIds.has(inbox.id)) return;
    loadedInboxIds.add(inbox.id);
    loadInboxBot(inbox);
    loadInboxHealth(inbox);
  });
};

const botConfigurationRoute = inbox => ({
  name: 'settings_inbox_show',
  params: { inboxId: inbox.id, tab: 'bot-configuration' },
});

const collaboratorsRoute = inbox => ({
  name: 'settings_inbox_show',
  params: { inboxId: inbox.id, tab: 'collaborators' },
});

const groupedFilteredInboxes = computed(() => {
  const groups = filteredInboxesList.value.reduce((acc, inbox) => {
    const category = getInboxCategory(inbox);
    if (!acc[category]) acc[category] = [];
    acc[category].push(inbox);
    return acc;
  }, {});

  return categoryOrder
    .filter(category => groups[category]?.length)
    .map(category => ({
      key: category,
      title: categoryLabels[category],
      description: categoryDescriptions[category],
      inboxes: groups[category],
    }));
});

const buildTargetOptions = sourceInbox => {
  if (!sourceInbox?.id) return [];

  return inboxesList.value
    .filter(inbox => inbox.id !== sourceInbox.id)
    .map(inbox => ({
      id: inbox.id,
      name: inbox.name,
      channelType: inbox.channel_type,
      compatible: isMigrationCompatible(sourceInbox, inbox),
    }));
};

const migrationTargetOptions = computed(() =>
  buildTargetOptions(migrationSourceInbox.value)
);

const migrationTargetSelectOptions = computed(() =>
  migrationTargetOptions.value.map(targetInbox => ({
    value: String(targetInbox.id),
    label: targetInbox.compatible
      ? targetInbox.name
      : `${targetInbox.name} (incompativel)`,
    disabled: !targetInbox.compatible,
  }))
);

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteTargetInboxOptions = computed(() =>
  buildTargetOptions(selectedInbox.value)
);

const deleteTargetSelectOptions = computed(() =>
  deleteTargetInboxOptions.value.map(targetInbox => ({
    value: String(targetInbox.id),
    label: targetInbox.compatible
      ? targetInbox.name
      : `${targetInbox.name} (incompativel)`,
    disabled: !targetInbox.compatible,
  }))
);

const isCompatibleTarget = (sourceInbox, targetInboxId) => {
  const selected = buildTargetOptions(sourceInbox).find(
    option => Number(option.id) === Number(targetInboxId)
  );

  return Boolean(selected?.compatible);
};

const deleteInbox = async ({ id }, deleteParams = {}) => {
  try {
    const result = await store.dispatch('inboxes/delete', { id, deleteParams });
    useAlert(
      result?.deletion_scheduled
        ? listCopy.deleteScheduledSuccess
        : t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE')
    );
    return true;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE')
    );
    return false;
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const openMigrate = inbox => {
  migrationSourceInbox.value = inbox;
  migrationTargetInboxId.value = null;
  showMigratePopup.value = true;
};

const closeMigrate = () => {
  showMigratePopup.value = false;
  deleteMode.value = 'migrate';
  deleteTargetInboxId.value = '';
  migrationSourceInbox.value = {};
  migrationTargetInboxId.value = null;
};

const confirmMigration = async () => {
  const targetInboxId = Number(migrationTargetInboxId.value);
  if (Number.isNaN(targetInboxId) || targetInboxId <= 0) {
    useAlert(listCopy.migrateValidationError);
    return;
  }

  if (!isCompatibleTarget(migrationSourceInbox.value, targetInboxId)) {
    useAlert(listCopy.incompatibleTargetError);
    return;
  }

  try {
    isMigratingData.value = true;
    await store.dispatch('inboxes/migrateData', {
      sourceInboxId: migrationSourceInbox.value.id,
      targetInboxId,
    });
    useAlert(listCopy.migrateSuccess);
    closeMigrate();
  } catch {
    useAlert(listCopy.migrateError);
  } finally {
    isMigratingData.value = false;
  }
};

const confirmDeletion = async () => {
  if (uiFlags.value.isDeleting) return;

  if (deleteMode.value === 'hard_delete') {
    const deleted = await deleteInbox(selectedInbox.value, {
      delete_all_contacts_and_messages: true,
    });
    if (deleted) closeDelete();
    return;
  }

  const targetInboxId = Number(deleteTargetInboxId.value);
  if (Number.isNaN(targetInboxId) || targetInboxId <= 0) {
    useAlert(listCopy.deleteValidationError);
    return;
  }

  if (!isCompatibleTarget(selectedInbox.value, targetInboxId)) {
    useAlert(listCopy.incompatibleTargetError);
    return;
  }

  const deleted = await deleteInbox(selectedInbox.value, {
    target_inbox_id: targetInboxId,
  });
  if (deleted) closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  deleteMode.value = 'migrate';
  deleteTargetInboxId.value = '';
  selectedInbox.value = inbox;
};

const getChannelIconFrame = inbox => useChannelIconFrame(inbox).value;

const hasInboxAvatar = inbox =>
  Boolean(inbox.avatar_url) && !failedInboxAvatarIds.value[inbox.id];

const markInboxAvatarAsFailed = inbox => {
  failedInboxAvatarIds.value = {
    ...failedInboxAvatarIds.value,
    [inbox.id]: true,
  };
};

watch(
  inboxesList,
  list => {
    loadInboxExtras(list);
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    // This screen must not depend on the cached request started by the sidebar.
    // A stalled browser cache/IndexedDB request used to leave it loading forever.
    await store.dispatch('inboxes/getFromNetwork');
  } catch {
    useAlert(t('INBOX_MGMT.LIST.FETCH_ERROR'));
  } finally {
    isLoadingInboxes.value = false;
  }

  loadInboxExtras(inboxesList.value);
});
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="isLoadingInboxes"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="flex flex-col gap-8">
        <section
          v-for="group in groupedFilteredInboxes"
          :key="group.key"
          class="flex flex-col gap-3"
        >
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <h2 class="mb-0 text-base font-semibold text-n-slate-12">
                {{ group.title }}
              </h2>
              <span
                class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
              >
                {{ group.inboxes.length }}
              </span>
            </div>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ group.description }}
            </p>
          </div>
          <div class="divide-y divide-n-weak border-y border-n-weak">
            <div
              v-for="inbox in group.inboxes"
              :key="inbox.id"
              class="flex flex-col gap-4 py-4 xl:flex-row xl:items-center xl:justify-between"
            >
              <div class="flex min-w-0 flex-1 flex-col gap-4 lg:flex-row">
                <div class="flex min-w-0 items-start gap-4">
                  <div
                    class="size-10 flex-shrink-0 justify-center overflow-hidden rounded-xl ring ring-n-solid-1 border shadow-sm grid place-items-center"
                    :class="getChannelIconFrame(inbox)"
                  >
                    <img
                      v-if="hasInboxAvatar(inbox)"
                      :src="inbox.avatar_url"
                      :alt="inbox.name"
                      class="object-cover size-full"
                      @error="markInboxAvatarAsFailed(inbox)"
                    />
                    <ChannelIcon
                      v-else
                      colored
                      use-brand-icon
                      class="size-6"
                      :inbox="inbox"
                    />
                  </div>
                  <div class="flex min-w-0 flex-col items-start gap-1">
                    <span
                      class="block max-w-full truncate text-heading-3 text-n-slate-12 capitalize"
                    >
                      {{ inbox.name }}
                    </span>
                    <ChannelName
                      :channel-type="inbox.channel_type"
                      :medium="inbox.medium"
                      :voice-enabled="inbox.voice_enabled"
                      class="text-body-main text-n-slate-11"
                    />
                    <span
                      v-if="isWhatsAppLikeInbox(inbox)"
                      class="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium"
                      :class="qualityClassForInbox(inbox)"
                    >
                      {{ qualityTextForInbox(inbox) }}
                    </span>
                  </div>
                </div>

                <div class="flex min-w-0 flex-1 items-center text-sm">
                  <div class="min-w-0 xl:w-64 2xl:w-72">
                    <span class="block text-xs font-medium text-n-slate-10">
                      {{ listCopy.connectedBot }}
                    </span>
                    <span
                      class="mt-1 block truncate text-base font-medium text-n-slate-12"
                    >
                      {{ activeBotName(inbox) }}
                    </span>
                  </div>
                </div>
              </div>

              <div
                class="flex flex-col gap-3 sm:flex-row sm:items-center xl:flex-shrink-0 xl:justify-end"
              >
                <div class="ml-auto flex justify-end gap-2">
                  <router-link v-if="isAdmin" :to="collaboratorsRoute(inbox)">
                    <Button
                      v-tooltip.top="listCopy.editCollaborators"
                      icon="i-lucide-users"
                      slate
                      sm
                    />
                  </router-link>
                  <router-link
                    v-if="isAdmin"
                    :to="botConfigurationRoute(inbox)"
                  >
                    <Button
                      v-tooltip.top="listCopy.editBot"
                      icon="i-lucide-bot"
                      slate
                      sm
                    />
                  </router-link>
                  <router-link
                    v-if="isAdmin"
                    :to="{
                      name: 'settings_inbox_show',
                      params: { inboxId: inbox.id },
                    }"
                  >
                    <Button
                      v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                      icon="i-lucide-settings"
                      slate
                      sm
                    />
                  </router-link>
                  <Button
                    v-if="isAdmin"
                    v-tooltip.top="listCopy.migrateData"
                    icon="i-lucide-arrow-right-left"
                    slate
                    sm
                    @click="openMigrate(inbox)"
                  />
                  <Button
                    v-if="isAdmin"
                    v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDelete(inbox)"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </template>

    <woot-modal v-model:show="showDeletePopup" @close="closeDelete">
      <div class="p-6 flex flex-col gap-5">
        <woot-modal-header
          :header-title="listCopy.deleteModalTitle"
          :header-content="listCopy.deleteModalDescription"
        />

        <div
          class="flex items-start gap-3 rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-3"
        >
          <span
            class="i-lucide-info mt-0.5 size-4 flex-shrink-0 text-n-slate-10"
          />
          <div class="min-w-0">
            <p
              class="mb-0 text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ listCopy.selectedInbox }}
            </p>
            <p class="mb-0 mt-0.5 truncate text-sm font-medium text-n-slate-12">
              {{ selectedInbox.name || '-' }}
            </p>
          </div>
        </div>

        <div class="flex flex-col gap-3">
          <span class="text-sm font-medium text-n-slate-12">
            {{ listCopy.deleteModeLabel }}
          </span>

          <label
            class="flex items-start gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-12 cursor-pointer"
          >
            <input
              v-model="deleteMode"
              type="radio"
              value="migrate"
              class="mt-0.5"
            />
            <span class="leading-5">{{ listCopy.deleteModeMigrate }}</span>
          </label>

          <label
            class="flex items-start gap-2 rounded-lg border border-n-ruby-5 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11 cursor-pointer"
          >
            <input
              v-model="deleteMode"
              type="radio"
              value="hard_delete"
              class="mt-0.5"
            />
            <span class="leading-5">{{ listCopy.deleteModeHardDelete }}</span>
          </label>
        </div>

        <div v-if="deleteMode === 'migrate'" class="flex flex-col gap-2">
          <label
            class="text-sm font-medium text-n-slate-12"
            for="delete_target_inbox"
          >
            {{ listCopy.deleteTargetLabel }}
          </label>
          <SelectInput
            id="delete_target_inbox"
            v-model="deleteTargetInboxId"
            class="!w-full"
            :placeholder="listCopy.deleteTargetPlaceholder"
            :options="deleteTargetSelectOptions"
          />
        </div>

        <div class="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button
            :label="listCopy.deleteCancel"
            type="button"
            faded
            slate
            sm
            :disabled="uiFlags.isDeleting"
            @click="closeDelete"
          />
          <Button
            :label="listCopy.deleteConfirm"
            type="button"
            sm
            :ruby="deleteMode === 'hard_delete'"
            :is-loading="uiFlags.isDeleting"
            :disabled="uiFlags.isDeleting"
            @click="confirmDeletion"
          />
        </div>
      </div>
    </woot-modal>

    <woot-modal v-model:show="showMigratePopup" @close="closeMigrate">
      <div class="p-6 flex flex-col gap-5">
        <woot-modal-header
          :header-title="listCopy.migrateModalTitle"
          :header-content="listCopy.migrateModalDescription"
        />

        <div class="flex flex-col gap-2">
          <label
            class="text-sm font-medium text-n-slate-12"
            for="migration_target_inbox"
          >
            {{ listCopy.migrateTargetLabel }}
          </label>
          <SelectInput
            id="migration_target_inbox"
            v-model="migrationTargetInboxId"
            class="!w-full"
            :placeholder="listCopy.migrateTargetPlaceholder"
            :options="migrationTargetSelectOptions"
          />
        </div>

        <div class="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button
            :label="listCopy.migrateCancel"
            type="button"
            faded
            slate
            sm
            :disabled="isMigratingData"
            @click="closeMigrate"
          />
          <Button
            :label="listCopy.migrateConfirm"
            type="button"
            sm
            :is-loading="isMigratingData"
            :disabled="isMigratingData"
            @click="confirmMigration"
          />
        </div>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>
